import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import {
  createInstallationDvr,
  deleteInstallationDvr,
  getInstallationDvrs,
  updateInstallationDvr,
} from "../../../services/installationService";
import { captureWizardError } from "./shared/wizardObservability";

const DEBOUNCE_MS = 600;

// Blank draft DVR; POST requires at least login + password.
function emptyDraft(tempId) {
  return {
    tempId,
    status: "incomplete",
    dvr_login: "",
    dvr_password: "",
    dvr_ipv4: "",
    dvr_rtsp_port: null,
    dvr_http_port: null,
    rtsp_url_suffix: "",
  };
}

const dvrKey = (dvr) => dvr?.id ?? dvr?.tempId;

function toastError(err, fallback) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const detail = data?.detail ?? data?.error ?? data?.code;
  if (status >= 400 && status < 500) toast.error(detail ?? fallback);
  else toast.error(fallback);
  captureWizardError(err, "dvr", { detail: fallback });
}

/**
 * DVR list state over the `/dvrs/` CRUD routes. Persisted DVRs have a numeric
 * `id` and autosave field edits via debounced PATCH; drafts have a `tempId` and
 * stay local until `createDvr` POSTs them.
 */
export default function useInstallationDvrs(initialInstallation) {
  const [dvrs, setDvrs] = useState(() => initialInstallation?.dvrs ?? []);

  const identifierRef = useRef(initialInstallation?.uid ?? initialInstallation?.deal_id);
  const mountedRef = useRef(true);
  const tempIdRef = useRef(0);
  // Pending PATCH payloads keyed by DVR id.
  const pendingRef = useRef({});
  // Synchronous mirror of `dvrs` for createDvr.
  const dvrsRef = useRef(dvrs);
  dvrsRef.current = dvrs;

  // PATCH each DVR's pending edits. No chaining: concurrent flushes are last-writer-wins.
  const flush = useCallback(() => {
    const identifier = identifierRef.current;
    const pending = pendingRef.current;
    pendingRef.current = {};
    Object.entries(pending).forEach(([dvrId, payload]) => {
      if (!identifier || Object.keys(payload).length === 0) return;
      updateInstallationDvr(identifier, dvrId, payload)
        .then(({ data }) => {
          if (!mountedRef.current || identifierRef.current !== identifier) return;
          setDvrs((prev) => prev.map((d) => (d.id === Number(dvrId) ? { ...d, ...data } : d)));
        })
        .catch((err) => {
          if (!mountedRef.current || identifierRef.current !== identifier) return;
          // Re-queue failed edits for the next flush.
          pendingRef.current[dvrId] = { ...payload, ...(pendingRef.current[dvrId] ?? {}) };
          debouncedFlush();
          toastError(err, "Failed to save DVR changes");
        });
    });
  }, []);

  const debouncedFlush = useMemo(() => debounce(flush, DEBOUNCE_MS), [flush]);

  // Reset on installation swap.
  useEffect(() => {
    const nextId = initialInstallation?.uid ?? initialInstallation?.deal_id;
    if (nextId !== identifierRef.current) {
      debouncedFlush.cancel();
      pendingRef.current = {};
      identifierRef.current = nextId;
      setDvrs(initialInstallation?.dvrs ?? []);
    }
  }, [initialInstallation, debouncedFlush]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      debouncedFlush.cancel();
      if (Object.keys(pendingRef.current).length > 0) flush();
    };
  }, [debouncedFlush, flush]);

  const addDraftDvr = useCallback(() => {
    tempIdRef.current += 1;
    const tempId = `draft-${tempIdRef.current}`;
    setDvrs((prev) => [...prev, emptyDraft(tempId)]);
  }, []);

  // Persisted: optimistic + debounced PATCH. Draft: local only.
  const setDvrField = useCallback(
    (key, field, value) => {
      setDvrs((prev) => prev.map((d) => (dvrKey(d) === key ? { ...d, [field]: value } : d)));
      if (typeof key === "number") {
        pendingRef.current[key] = { ...(pendingRef.current[key] ?? {}), [field]: value };
        debouncedFlush();
      }
    },
    [debouncedFlush]
  );

  // POST a draft DVR with its filled fields.
  const createDvr = useCallback(
    async (tempId) => {
      const identifier = identifierRef.current;
      const draft = dvrsRef.current.find((d) => d.tempId === tempId);
      if (!identifier || !draft) return;
      const { tempId: _tempId, status: _status, ...fields } = draft;
      const payload = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== null && v !== ""));
      try {
        const { data } = await createInstallationDvr(identifier, payload);
        if (!mountedRef.current) return;
        // Preserve (and PATCH) edits made while the POST was in flight.
        const latest = dvrsRef.current.find((d) => d.tempId === tempId);
        const lateEdits = {};
        Object.keys(fields).forEach((field) => {
          const value = latest?.[field];
          if (latest && value !== draft[field] && value !== null && value !== "") lateEdits[field] = value;
        });
        setDvrs((prev) => prev.map((d) => (d.tempId === tempId ? { ...data, ...lateEdits } : d)));
        if (data?.id != null && Object.keys(lateEdits).length > 0) {
          pendingRef.current[data.id] = { ...(pendingRef.current[data.id] ?? {}), ...lateEdits };
          debouncedFlush();
        }
      } catch (err) {
        if (!mountedRef.current) return;
        toastError(err, "Failed to create DVR");
      }
    },
    [debouncedFlush]
  );

  const reloadDvrs = useCallback(async () => {
    const identifier = identifierRef.current;
    if (!identifier) return;
    try {
      const { data } = await getInstallationDvrs(identifier);
      if (!mountedRef.current || identifierRef.current !== identifier) return;
      setDvrs(data ?? []);
    } catch (err) {
      if (!mountedRef.current) return;
      toastError(err, "Failed to refresh DVRs");
    }
  }, []);

  const removeDvr = useCallback(async (key) => {
    const identifier = identifierRef.current;
    if (typeof key !== "number") {
      // Draft: drop locally.
      setDvrs((prev) => prev.filter((d) => d.tempId !== key));
      return;
    }
    if (!identifier) return;
    delete pendingRef.current[key];
    try {
      await deleteInstallationDvr(identifier, key);
      if (!mountedRef.current) return;
      setDvrs((prev) => prev.filter((d) => d.id !== key));
    } catch (err) {
      if (!mountedRef.current) return;
      toastError(err, "Failed to delete DVR");
    }
  }, []);

  return { dvrs, addDraftDvr, createDvr, setDvrField, removeDvr, reloadDvrs };
}
